import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import useProjectFeatureEnabled from '@/hooks/useProjectFeatureEnabled'
import { Document } from '@/types/documents'
import { WorkflowState } from '@/types/workflows'
import { useSelectedWorkflowContext } from '@/components/workflows/SelectedWorkflowProvider'

type DocumentCurrentStateViewProviderValue = {
  allowedDocumentCurrentStateViews: WorkflowState['allowed_document_views']
  defaultDocumentStateWorkflowView?: WorkflowState['default_document_view']
  documentCurrentStateView?: WorkflowState['default_document_view']
  setDocumentCurrentStateView: (
    view: WorkflowState['default_document_view'],
  ) => void
  currentState?: WorkflowState
}

const DocumentCurrentStateViewContext =
  createContext<DocumentCurrentStateViewProviderValue>(
    {} as DocumentCurrentStateViewProviderValue,
  )

export const useDocumentCurrentStateViewContext = () =>
  useContext(DocumentCurrentStateViewContext)

type DocumentCurrentStateViewProviderProps = {
  children: ReactNode
  document?: Document
}

export default function DocumentCurrentStateViewProvider({
  children,
  document,
}: DocumentCurrentStateViewProviderProps) {
  const { selectedWorkflow } = useSelectedWorkflowContext()
  const {
    isEnabled: isPurchaseOrdersEnabled,
    isPending: isPurchaseOrdersPending,
  } = useProjectFeatureEnabled('purchase-orders')

  // While the purchase-orders flag is still resolving, `isEnabled` is falsy
  // (the exists query has no data yet) — treating that as "disabled" would prematurely drop the
  // purchase-order view and coerce a purchase-order default to `validation`,
  // which then sticks (see sc-24709). Treat a pending flag as optimistically
  // enabled so the correct default is committed on the first render; if the flag
  // later resolves false, the reconcile effect below snaps the now-invalid view
  // back to a valid fallback.
  const isPurchaseOrdersActiveOrPending =
    isPurchaseOrdersEnabled || isPurchaseOrdersPending

  const documentState = useMemo(
    () =>
      selectedWorkflow.workflow_states?.find((state) =>
        document?.workflow_states_ids?.includes(state.id),
      ),
    [document, selectedWorkflow.workflow_states],
  )

  const allowedDocumentCurrentStateViews = useMemo(() => {
    const filtered = (documentState?.allowed_document_views || []).filter(
      (view) => isPurchaseOrdersActiveOrPending || view !== 'purchase-order',
    )
    return filtered.length > 0
      ? filtered
      : (['validation'] as WorkflowState['allowed_document_views'])
  }, [documentState?.allowed_document_views, isPurchaseOrdersActiveOrPending])

  const defaultDocumentStateWorkflowView =
    documentState?.default_document_view === 'purchase-order' &&
    !isPurchaseOrdersActiveOrPending
      ? allowedDocumentCurrentStateViews[0]
      : documentState?.default_document_view

  const [documentCurrentStateView, setDocumentCurrentStateView] = useState(
    defaultDocumentStateWorkflowView,
  )

  // Identifies the workflow-state context whose view is currently committed. The
  // provider is not remounted between documents (it wraps the whole overlay), so
  // `useState`'s initializer only runs once and the committed view otherwise
  // carries over. When a context that should default to the purchase-order view
  // inherits the previous, still-allowed validation view, the wrong view sticks
  // (see sc-23935) — so whenever the workflow state changes we snap to that
  // state's default view.
  //
  // The key is intentionally the workflow *state*, not the document: the default
  // and allowed views are derived from the state, so two documents sharing a
  // state share a default. Switching to another document in the *same* workflow
  // state therefore preserves the user's current view (including a manual,
  // non-default selection) rather than resetting it. A state change — whether
  // from moving the same document forward or from opening a document in a
  // different state — still resets to the new state's default.
  const viewContextKey = documentState?.id ?? ''
  const previousViewContextKeyRef = useRef(viewContextKey)

  useEffect(() => {
    const contextChanged = previousViewContextKeyRef.current !== viewContextKey
    previousViewContextKeyRef.current = viewContextKey

    setDocumentCurrentStateView((prev) => {
      if (
        ((contextChanged || !prev) && defaultDocumentStateWorkflowView) ||
        (prev && !allowedDocumentCurrentStateViews.includes(prev))
      ) {
        return defaultDocumentStateWorkflowView
      }
      return prev
    })
  }, [
    viewContextKey,
    allowedDocumentCurrentStateViews,
    defaultDocumentStateWorkflowView,
  ])

  return (
    <DocumentCurrentStateViewContext.Provider
      value={{
        allowedDocumentCurrentStateViews,
        defaultDocumentStateWorkflowView,
        documentCurrentStateView,
        setDocumentCurrentStateView,
        currentState: documentState,
      }}
    >
      {children}
    </DocumentCurrentStateViewContext.Provider>
  )
}
